import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaSearch, FaTimes, FaCheck } from 'react-icons/fa';
import { GENERAL_CATEGORY_OPTIONS } from '../constants/categories';
import './SearchableCheckboxPicker.css';

function PickerDescriptionText({ description, tipId }) {
    const textRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el) {
            setIsTruncated(false);
            return undefined;
        }

        const checkOverflow = () => {
            setIsTruncated(el.scrollWidth > el.clientWidth + 1);
        };

        checkOverflow();
        const observer = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(checkOverflow)
            : null;
        observer?.observe(el);
        window.addEventListener('resize', checkOverflow);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', checkOverflow);
        };
    }, [description]);

    const descriptionEl = (
        <span
            ref={textRef}
            className="picker-item-description"
            style={{
                display: 'block',
                fontSize: '0.75rem',
                color: 'var(--theme-text-muted)',
                marginTop: '3px',
                lineHeight: 1.35,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: isTruncated ? 'help' : 'inherit',
            }}
        >
            {description}
        </span>
    );

    if (!isTruncated) {
        return descriptionEl;
    }

    return (
        <OverlayTrigger
            placement="top"
            delay={{ show: 150, hide: 50 }}
            overlay={
                <Tooltip id={`picker-desc-tip-${tipId}`}>
                    <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', display: 'inline-block', maxWidth: 280, textAlign: 'left' }}>
                        {description}
                    </span>
                </Tooltip>
            }
        >
            <span style={{ display: 'block', minWidth: 0 }}>
                {descriptionEl}
            </span>
        </OverlayTrigger>
    );
}

/**
 * Full-width multi-select list with search + optional general-category filter.
 * Used when adding activities to a service/care plan or price items to a quotation.
 */
function SearchableCheckboxPicker({
    label = 'Select Items',
    searchPlaceholder = 'Search...',
    items = [],
    getItemId,
    getItemLabel,
    getItemDescription,
    getItemCategory = (item) => item.general_category,
    isDefaultItem,
    isAlreadyAdded,
    selectedItems = [],
    onSelectionChange,
    defaultSectionTitle = 'Default (Recommended)',
    allSectionTitle = 'All Items',
    selectedNoun = 'item',
    showDefaultBadge = false,
    enableCategoryFilter = true,
}) {
    const [pickerSearch, setPickerSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    const availableCategories = useMemo(() => {
        if (!enableCategoryFilter) return [];

        const present = new Set();
        let hasUncategorized = false;

        items.forEach((item) => {
            const category = String(getItemCategory(item) || '').trim();
            if (category) {
                present.add(category);
            } else {
                hasUncategorized = true;
            }
        });

        const distinctBuckets = present.size + (hasUncategorized ? 1 : 0);
        if (distinctBuckets < 2) return [];

        const ordered = GENERAL_CATEGORY_OPTIONS.filter((cat) => present.has(cat));
        const extras = [...present]
            .filter((cat) => !GENERAL_CATEGORY_OPTIONS.includes(cat))
            .sort((a, b) => a.localeCompare(b));

        const options = [...ordered, ...extras];
        if (hasUncategorized) {
            options.push('__uncategorized__');
        }
        return options;
    }, [items, getItemCategory, enableCategoryFilter]);

    const filteredItems = useMemo(() => {
        const term = pickerSearch.trim().toLowerCase();

        return items.filter((item) => {
            if (categoryFilter) {
                const category = String(getItemCategory(item) || '').trim();
                if (categoryFilter === '__uncategorized__') {
                    if (category) return false;
                } else if (category !== categoryFilter) {
                    return false;
                }
            }

            if (!term) return true;
            const name = String(getItemLabel(item) || '').toLowerCase();
            const description = String(getItemDescription?.(item) || '').toLowerCase();
            return name.includes(term) || description.includes(term);
        });
    }, [items, pickerSearch, categoryFilter, getItemLabel, getItemDescription, getItemCategory]);

    const defaultFiltered = useMemo(() => {
        if (!isDefaultItem) return [];
        const seen = new Set();
        return filteredItems.filter((item) => {
            if (!isDefaultItem(item)) return false;
            const id = String(getItemId(item));
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }, [filteredItems, isDefaultItem, getItemId]);

    const otherFiltered = useMemo(() => {
        const defaultIds = new Set(defaultFiltered.map((item) => String(getItemId(item))));
        const seen = new Set();
        return filteredItems.filter((item) => {
            const id = String(getItemId(item));
            if (defaultIds.has(id)) return false;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }, [filteredItems, defaultFiltered, getItemId]);

    const isSelected = (item) =>
        selectedItems.some((s) => String(getItemId(s)) === String(getItemId(item)));

    const toggleItem = (item, checked) => {
        if (isAlreadyAdded?.(item)) return;
        if (checked) {
            onSelectionChange([...selectedItems, item]);
        } else {
            onSelectionChange(
                selectedItems.filter((s) => String(getItemId(s)) !== String(getItemId(item)))
            );
        }
    };

    const clearFilters = () => {
        setPickerSearch('');
        setCategoryFilter('');
    };

    const renderRow = (item, { isDefault }) => {
        const alreadyAdded = Boolean(isAlreadyAdded?.(item));
        const checked = isSelected(item);
        const id = getItemId(item);
        const category = String(getItemCategory(item) || '').trim();

        return (
            <label
                key={id}
                className="picker-item"
                data-selected={checked || undefined}
                data-default={isDefault || undefined}
                data-added={alreadyAdded || undefined}
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    padding: '10px 12px',
                    margin: 0,
                    cursor: alreadyAdded ? 'default' : 'pointer',
                    borderRadius: '6px',
                    border: `1px solid ${
                        checked
                            ? isDefault
                                ? '#86c99a'
                                : '#90b8e0'
                            : '#e9ecef'
                    }`,
                    backgroundColor: checked
                        ? isDefault
                            ? '#f0faf3'
                            : '#f0f6fc'
                        : alreadyAdded
                          ? '#f8f9fa'
                          : '#fff',
                    opacity: alreadyAdded ? 0.65 : 1,
                    transition: 'border-color 0.12s ease, background-color 0.12s ease',
                }}
            >
                <input
                    type="checkbox"
                    checked={checked}
                    disabled={alreadyAdded}
                    style={{
                        cursor: alreadyAdded ? 'default' : 'pointer',
                        width: '15px',
                        height: '15px',
                        flexShrink: 0,
                        marginTop: '2px',
                    }}
                    onChange={(e) => toggleItem(item, e.target.checked)}
                />
                <span style={{ minWidth: 0, flex: 1 }}>
                    <span
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flexWrap: 'wrap',
                            fontSize: '0.9rem',
                            fontWeight: 500,
                            color: alreadyAdded ? 'var(--theme-text-muted)' : 'var(--theme-text)',
                            lineHeight: 1.35,
                        }}
                    >
                        <span style={{ wordBreak: 'break-word' }}>{getItemLabel(item)}</span>
                        {alreadyAdded && (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: '#198754',
                                    backgroundColor: '#e8f5e9',
                                    borderRadius: '4px',
                                    padding: '1px 6px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.02em',
                                }}
                            >
                                <FaCheck style={{ fontSize: '0.6rem' }} /> Added
                            </span>
                        )}
                        {!alreadyAdded && showDefaultBadge && isDefault && (
                            <span
                                style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: '#198754',
                                    backgroundColor: '#e8f5e9',
                                    borderRadius: '4px',
                                    padding: '1px 6px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.02em',
                                }}
                            >
                                Default
                            </span>
                        )}
                    </span>
                    {(() => {
                        const description = String(getItemDescription?.(item) || '').trim();
                        if (!description) return null;
                        return <PickerDescriptionText description={description} tipId={id} />;
                    })()}
                    {category && !categoryFilter && (
                        <span
                            style={{
                                display: 'block',
                                fontSize: '0.75rem',
                                color: 'var(--theme-text-muted)',
                                marginTop: '3px',
                            }}
                        >
                            {category}
                        </span>
                    )}
                </span>
            </label>
        );
    };

    const renderSection = (title, sectionItems, { isDefault, showCount }) => {
        if (sectionItems.length === 0 && !isDefault) return null;
        if (isDefault && sectionItems.length === 0) return null;

        return (
            <div style={{ marginBottom: isDefault ? '14px' : 0 }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '8px',
                        padding: '0 2px',
                    }}
                >
                    <span
                        style={{
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase',
                            color: isDefault ? '#198754' : '#6c757d',
                        }}
                    >
                        {title}
                    </span>
                    {showCount && (
                        <span style={{ fontSize: '0.75rem', color: '#adb5bd' }}>
                            {sectionItems.length}
                        </span>
                    )}
                </div>
                {sectionItems.length === 0 ? (
                    <div style={{ padding: '8px 2px', color: '#adb5bd', fontSize: '0.85rem' }}>
                        No other matches
                    </div>
                ) : (
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                            gap: '8px',
                        }}
                    >
                        {sectionItems.map((item) => renderRow(item, { isDefault }))}
                    </div>
                )}
            </div>
        );
    };

    const hasDefaults = defaultFiltered.length > 0;
    const hasResults = filteredItems.length > 0;
    const hasActiveFilters = Boolean(pickerSearch.trim() || categoryFilter);
    const nounPlural =
        selectedItems.length === 1
            ? selectedNoun
            : selectedNoun.endsWith('y')
              ? `${selectedNoun.slice(0, -1)}ies`
              : `${selectedNoun}s`;

    const emptyMessage = () => {
        if (pickerSearch.trim() && categoryFilter) {
            const catLabel =
                categoryFilter === '__uncategorized__' ? 'Uncategorized' : categoryFilter;
            return `No results for “${pickerSearch.trim()}” in ${catLabel}`;
        }
        if (pickerSearch.trim()) {
            return `No results for “${pickerSearch.trim()}”`;
        }
        if (categoryFilter) {
            const catLabel =
                categoryFilter === '__uncategorized__' ? 'Uncategorized' : categoryFilter;
            return `No ${selectedNoun}s in ${catLabel}`;
        }
        return 'No items available';
    };

    return (
        <div style={{ width: '100%', minWidth: 0 }}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: '12px',
                    marginBottom: '8px',
                }}
            >
                <Form.Label className="mb-0 fw-semibold" style={{ fontSize: '0.9rem', color: 'var(--theme-text)' }}>
                    {label}
                </Form.Label>
                {selectedItems.length > 0 && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary)', fontWeight: 500 }}>
                        {selectedItems.length} {nounPlural} selected
                    </span>
                )}
            </div>

            <div
                style={{
                    border: '1px solid var(--theme-border)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--theme-surface)',
                    overflow: 'hidden',
                }}
            >
                <div
                    style={{
                        padding: '12px 14px',
                        borderBottom: '1px solid #e9ecef',
                        backgroundColor: 'var(--theme-surface)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        alignItems: 'center',
                    }}
                >
                    <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 0 }}>
                        <FaSearch
                            style={{
                                position: 'absolute',
                                left: '11px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: '#adb5bd',
                                fontSize: '0.8rem',
                                pointerEvents: 'none',
                            }}
                        />
                        <Form.Control
                            type="text"
                            size="sm"
                            placeholder={searchPlaceholder}
                            value={pickerSearch}
                            onChange={(e) => setPickerSearch(e.target.value)}
                            style={{
                                paddingLeft: '34px',
                                paddingRight: pickerSearch ? '32px' : undefined,
                                borderRadius: '6px',
                                borderColor: '#ced4da',
                            }}
                            aria-label={searchPlaceholder}
                        />
                        {pickerSearch && (
                            <button
                                type="button"
                                onClick={() => setPickerSearch('')}
                                aria-label="Clear search"
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    border: 'none',
                                    background: 'transparent',
                                    color: '#adb5bd',
                                    padding: '4px',
                                    cursor: 'pointer',
                                    lineHeight: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <FaTimes style={{ fontSize: '0.75rem' }} />
                            </button>
                        )}
                    </div>

                    {enableCategoryFilter && availableCategories.length > 0 && (
                        <Form.Select
                            size="sm"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            aria-label="Filter by general category"
                            style={{
                                borderRadius: '6px',
                                flex: '0 1 200px',
                                minWidth: '160px',
                                borderColor: '#ced4da',
                            }}
                        >
                            <option value="">All categories</option>
                            {availableCategories.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat === '__uncategorized__' ? 'Uncategorized' : cat}
                                </option>
                            ))}
                        </Form.Select>
                    )}

                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={clearFilters}
                            style={{
                                border: 'none',
                                background: 'transparent',
                                color: 'var(--theme-text-muted)',
                                fontSize: '0.8rem',
                                padding: '4px 6px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Clear filters
                        </button>
                    )}
                </div>

                <div style={{ maxHeight: '340px', overflowY: 'auto', padding: '12px 14px' }}>
                    {!hasResults ? (
                        <div
                            style={{
                                padding: '36px 16px',
                                textAlign: 'center',
                                color: 'var(--theme-text-muted)',
                                fontSize: '0.875rem',
                            }}
                        >
                            {emptyMessage()}
                        </div>
                    ) : (
                        <>
                            {hasDefaults &&
                                renderSection(defaultSectionTitle, defaultFiltered, {
                                    isDefault: true,
                                    showCount: true,
                                })}
                            {renderSection(
                                hasDefaults
                                    ? allSectionTitle.replace(/^All\b/, 'Other')
                                    : allSectionTitle,
                                otherFiltered,
                                {
                                    isDefault: false,
                                    showCount: hasActiveFilters || hasDefaults,
                                }
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SearchableCheckboxPicker;
